import {
  Box,
  Divider,
  Flex,
  Price,
  PrimaryButton,
  Spinner,
  Text,
} from "flicket-ui";
import { motion } from "framer-motion";
import { useContext, useMemo, useState } from "react";
import { UserContext } from "~context";
import { useReservationStore } from "~features/seated-reservation/store";
import * as actions from "~features/seated-reservation/store/actions/index";
import { Permission, ReferralCampaign, TicketTypeKind } from "~graphql/sdk";
import { useOrganization, usePermissions } from "~hooks";
import BuyNowPayLater from "../common/common.BuyNowPayLater";
import { AddonListItem } from "./pricingItems/PricingListItemAddon";
import { ListItem } from "./pricingItems/PricingListItem";
import Delayed from "~components/reservation/common/delayed";
import { getAfterPayLocaleByCurrency } from "~config";
import { QuantitySetValidationModal } from "~components/reservation/common/common.QuantitySetValidation.modal";
import { SeatsIOSeat } from "~components/reservation/constants";
import { PaymentPlanBadge } from "../common/common.PaymentPlanBadge";
import { Cart, OrderFee } from "~features/checkout/cart";
import { OrganizationFeatures } from "~lib";
import { useSeekaConverge } from "@seeka-labs/converge-react";

type QuantitySetValidation = {
  count: number;
  quantitySet: number;
  valid: boolean;
  name: string;
};

export const Pricing = ({
  type,
  changingSeatsFee,
  addons,
  changeTickets,
  navigateCheckout,
  isNavigating,
  onBack,
  bnplGateways,
  priceComp,
  allowPublicComp,
  activeReferralCampaign,
  stripeAfterpayGateway,
  paymentPlanSettings,
  fees,
}: {
  activeReferralCampaign: ReferralCampaign;
  fees: OrderFee;
  navigateCheckout: () => Promise<void>;
} & Partial<React.ComponentProps<any>>) => {
  const converge = useSeekaConverge();
  const { organization, hasFeature } = useOrganization();
  const checkPermissions = usePermissions();
  const { isPOS, isAdmin, user } = useContext(UserContext);

  const [quantitySetValidationError, setQuantitySetValidationError] = useState<{
    name: string;
    quantitySet: number;
  } | null>(null);
  const noSeatsAvailable = useReservationStore((state) => state.selectFailed);
  const busyState = useReservationStore((state) => state.busyState);
  const seats = useReservationStore((state) => state.seats);
  const selectedAddons = useReservationStore((state) => state.addons);
  const ticketTypes = useReservationStore((state) => state.eventTicketTypes);
  const availableTicketTypes = useReservationStore(
    (state) => state.availableTicketTypes
  );
  const multiBuyPromotions = useReservationStore(
    (state) => state.multiBuyPromotions
  );

  const showHiddenFees = hasFeature(OrganizationFeatures.ShowHiddenFees);

  const cart = new Cart(fees, multiBuyPromotions);
  cart.isPos(isPOS);
  if (!hasFeature(OrganizationFeatures.OrderSmsConfirmationDisabled)) {
    cart.sendSmsConfirmation(!(isAdmin || isPOS));
  }
  cart.includeFees(showHiddenFees);

  if (activeReferralCampaign) {
    cart.setReferralCampaign(activeReferralCampaign);
  }

  for (const seat of seats) {
    const id = seat.ticketType?.id || seat.ticketType?.value;

    if (id) {
      cart.add(id as string, seat.originalPrice);
    }
  }

  for (const [id, quantity] of Object.entries(selectedAddons ?? {})) {
    const addon = addons.find((a) => a.id === id);

    if (addon?.price) {
      cart.add(id, addon.price, quantity);
    }
  }

  const validSeatSelection = useReservationStore(
    (state) => state.validSeatSelection
  );

  const userCredits = user?.credits ?? 0;

  const ticketingOptions = useMemo((): any[] => {
    // Filter entire list of ticket types by permissions
    let filteredTicketTypes =
      (isAdmin && checkPermissions(Permission.OrderComp)) || allowPublicComp
        ? ticketTypes
        : ticketTypes?.filter(({ kind }) => kind === TicketTypeKind.Standard);

    // Filter the above ticket types further based on
    // the current Zone or Section availability
    filteredTicketTypes = filteredTicketTypes?.filter(
      (ticketType) =>
        !!availableTicketTypes?.find(
          ({ id, ticketTypeId, isActive }) =>
            (ticketType?.id === id || ticketType?.id === ticketTypeId) &&
            (typeof isActive !== "undefined" ? isActive : true)
        )
    );

    return filteredTicketTypes?.length
      ? filteredTicketTypes.map(({ id, name, ...rest }) => ({
          label: name,
          value: id,
          ...rest,
        }))
      : [];
  }, [ticketTypes, availableTicketTypes, isAdmin]);

  const availableAddons = useMemo(
    () => addons?.filter(({ isActive }) => isActive),
    [addons]
  );

  const updateSeatType = (seatId, ticketType) => {
    actions.updateSeatType({ seatId, ticketType }, converge);
  };

  const updateAddon = (id, quantity) => {
    actions.updateAddon({ id, quantity });
  };

  let pricingOptions;

  if (busyState === "finding-best-seats") {
    return (
      <motion.div
        key="spinner"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
      >
        <Spinner size={48} color="P300" mb={2} />
      </motion.div>
    );
  } else if (seats?.length) {
    pricingOptions = seats?.map((seat, i) => (
      <ListItem
        seat={seat}
        handleTicketTypeChange={updateSeatType}
        options={ticketingOptions}
        key={i}
        showHiddenFees={showHiddenFees}
      />
    ));
  } else if (type === "membership") {
    pricingOptions = <Text mb={3}>Please select your seating</Text>;
  } else if (noSeatsAvailable) {
    pricingOptions = (
      <>
        <Text mb={3}>Not enough seats available in this zone</Text>
        <PrimaryButton
          onClick={onBack}
          boxShadow="button"
          mt={{ _: "6/4", md: 1 }}
          w="100%"
        >
          Clear selection
        </PrimaryButton>
      </>
    );
  }

  const validateQuantitySets = (
    seats: SeatsIOSeat[],
    setQuantitySetValidationError: (error: {
      name: string;
      quantitySet: number;
    }) => void
  ): boolean => {
    const validationMap = new Map<string, QuantitySetValidation>();

    for (const { ticketType } of seats) {
      if (!ticketType?.quantitySet) continue;
      const currentEntry = validationMap.get(ticketType.id);
      const currentCount = currentEntry?.count || 0;
      const newCount = currentCount + 1;
      const isValid = newCount % ticketType.quantitySet === 0;

      validationMap.set(ticketType.id, {
        count: newCount,
        valid: isValid,
        quantitySet: ticketType.quantitySet,
        name: ticketType.label ?? ticketType.name,
      });
    }

    for (const entry of validationMap.values()) {
      if (!entry.valid) {
        setQuantitySetValidationError({
          name: entry.name,
          quantitySet: entry.quantitySet,
        });
        return false;
      }
    }

    return true;
  };

  const handleBuyButtonClicked = async () => {
    if (!seats) return;

    const isValid = validateQuantitySets(seats, setQuantitySetValidationError);

    if (isValid) {
      return navigateCheckout();
    }
  };

  return (
    <motion.div
      key="prices"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      <QuantitySetValidationModal
        isOpen={!!quantitySetValidationError}
        onClose={() => setQuantitySetValidationError(null)}
        quantitySet={quantitySetValidationError?.quantitySet}
        name={quantitySetValidationError?.name}
      />
      <Box>
        <Flex justifyContent={"space-between"} alignItems="baseline" mb={4}>
          <Text
            fontWeight="heavy"
            fontSize={5}
            color="N800"
            lineHeight="normal"
          >
            Your selection
          </Text>
        </Flex>

        {!seats?.length && (
          <Box mb={2} mt={4}>
            <Text fontWeight="extraBold" fontSize={3} color="N700">
              No seats selected
            </Text>
          </Box>
        )}

        {pricingOptions}

        <Box>
          {!noSeatsAvailable && (seats?.length || type === "membership") && (
            <>
              {!!availableAddons?.length && (
                <Box mb={2} mt={4}>
                  <Text
                    fontWeight="heavy"
                    fontSize={6}
                    color="N800"
                    lineHeight="normal"
                  >
                    Available Addons
                  </Text>
                  <Divider my={2} />
                  {availableAddons.map(
                    ({
                      id,
                      name,
                      price,
                      description,
                      restrictions,
                      addonImage,
                    }) => (
                      <AddonListItem
                        description={description}
                        restrictions={restrictions}
                        isAdmin={isAdmin}
                        addonImage={addonImage}
                        key={id}
                        id={id}
                        name={name}
                        price={price}
                        quantity={selectedAddons?.[id]}
                        onChange={(quanity) => {
                          updateAddon(id, quanity);
                        }}
                      />
                    )
                  )}
                </Box>
              )}
              {!!changeTickets?.length && (
                <Box mb={2}>
                  <Divider />
                  {!!changingSeatsFee && (
                    <Flex mt={2} justifyContent="space-between" w="100%">
                      <Text fontWeight="extraBold" fontSize={3} color="N700">
                        Reservation change fee
                      </Text>
                      <Text fontWeight="medium" fontSize={2} color="N600">
                        <Price price={changingSeatsFee} />
                      </Text>
                    </Flex>
                  )}

                  {!!priceComp && (
                    <Flex mt={2} justifyContent="space-between" w="100%">
                      <Text fontWeight="extraBold" fontSize={3} color="N700">
                        Price difference compensation
                      </Text>
                      <Text fontWeight="medium" fontSize={2} color="N600">
                        <Price price={-priceComp} />
                      </Text>
                    </Flex>
                  )}
                </Box>
              )}
              <Divider />
              <Flex mt={2} justifyContent="space-between" w="100%">
                <Text fontWeight="medium" fontSize={2} color="N600">
                  Total tickets
                </Text>
                <Text
                  fontWeight="medium"
                  fontSize={4}
                  lineHeight={"18px" as any}
                  color={"black" as any}
                >
                  {seats?.length}
                </Text>
              </Flex>

              {cart.multibuyDiscount > 0 && (
                <>
                  <Flex mt={2} justifyContent="space-between" w="100%">
                    <Text fontWeight="medium" fontSize={2} color="N600">
                      Multibuy promotion applied
                    </Text>
                    <Price
                      price={-cart.multibuyDiscount}
                      fontWeight="medium"
                      fontSize={4}
                      lineHeight={"18px" as any}
                      color={"black" as any}
                    />
                  </Flex>
                </>
              )}

              {cart.referralDiscount > 0 && (
                <>
                  <Flex mt={2} justifyContent="space-between" w="100%">
                    <Text fontWeight="medium" fontSize={2} color="N600">
                      Referral discount applied
                    </Text>
                    <Price
                      price={-cart.referralDiscount}
                      fontWeight="medium"
                      fontSize={4}
                      lineHeight={"18px" as any}
                      color={"black" as any}
                    />
                  </Flex>
                </>
              )}

              {(cart.multibuyDiscount > 0 || cart.referralDiscount > 0) && (
                <Divider my={{ _: 3, md: 2 }} />
              )}

              <Flex mt={2} justifyContent="space-between" w="100%">
                <Text fontWeight="medium" fontSize={2} color="N600">
                  Total {showHiddenFees ? "(including booking fees)" : ""}
                </Text>
                <Text
                  fontWeight="medium"
                  fontSize={4}
                  lineHeight={"18px" as any}
                  color={"black" as any}
                >
                  {seats ? <Price price={cart.total} /> : "-"}
                </Text>
              </Flex>
              {userCredits > 0 && seats && (
                <>
                  <Flex mt={2} justifyContent="space-between" w="100%">
                    <Text fontWeight="medium" fontSize={2} color="N600">
                      Your credits to be deducted
                    </Text>
                    <Text
                      fontWeight="medium"
                      fontSize={4}
                      lineHeight={"18px" as any}
                      color={"black" as any}
                    >
                      {`- $${user.credits.toFixed(2)}`}
                    </Text>
                  </Flex>

                  <Flex mt={2} justifyContent="space-between" w="100%">
                    <Text fontWeight="medium" fontSize={2} color="N600">
                      Total left to pay after deducting credits
                    </Text>
                    <Text
                      fontWeight="medium"
                      fontSize={4}
                      lineHeight={"18px" as any}
                      color={"black" as any}
                    >
                      {cart.total - user.credits > 0 ? (
                        <Price price={cart.total - user.credits} />
                      ) : (
                        "$0.00"
                      )}
                    </Text>
                  </Flex>
                </>
              )}

              {!isPOS && cart.total - userCredits > 0 && (
                <>
                  {bnplGateways?.length > 0 &&
                    bnplGateways.map((g) => (
                      <Box mt={2} w="100%" key={g?.type}>
                        <BuyNowPayLater
                          totalPrice={cart.total - userCredits}
                          hasTicketSelected={!!seats.length}
                          showHiddenFees={showHiddenFees}
                          isSmallDisplay={true}
                          bnplIntegration={g}
                        />
                      </Box>
                    ))}
                  {stripeAfterpayGateway && (
                    <Box
                      justifyContent="center"
                      width="100%"
                      textAlign={"center"}
                      mt={3}
                    >
                      <Text as={"span"} variant="regular">
                        AfterPay available
                      </Text>
                      {/* @ts-expect-error - Afterpay placement component */}
                      <afterpay-placement
                        data-intro-text="Pay in"
                        data-locale={getAfterPayLocaleByCurrency(
                          organization.currency
                        )}
                        data-currency={organization.currency}
                        data-amount={cart.total - userCredits}
                      >
                        {/* @ts-expect-error - Afterpay placement component */}
                      </afterpay-placement>
                    </Box>
                  )}
                  {paymentPlanSettings && !paymentPlanSettings.disabledAt && (
                    <PaymentPlanBadge
                      maxInstallmentCount={paymentPlanSettings?.scheduleOptions
                        ?.map(
                          (s: { installmentCount: number }) =>
                            s?.installmentCount
                        )
                        ?.reduce((a, b) => Math.max(a, b))}
                    />
                  )}
                </>
              )}
              <PrimaryButton
                boxShadow="button"
                mt={{ _: "6/4", md: 4 }}
                onClick={() => {
                  void handleBuyButtonClicked();
                }}
                isLoading={isNavigating}
                disabled={
                  !ticketingOptions?.length ||
                  !seats?.length ||
                  (validSeatSelection === false && !isAdmin && !isPOS)
                }
                w="100%"
                fontSize={4}
              >
                {validSeatSelection === true || isAdmin || isPOS
                  ? "Buy tickets"
                  : "Can not leave a single seat"}
              </PrimaryButton>
              {isNavigating && (
                <Delayed waitBeforeShowMs={1500}>
                  <Text mt={2} fontSize={2} color="N600" textAlign={"center"}>
                    Sit tight, we&apos;re securing your seats...
                  </Text>
                </Delayed>
              )}
            </>
          )}
        </Box>
      </Box>
    </motion.div>
  );
};
